import { apiClient } from "@/services/apiClient";

export type ProfessionalPricingDto = {
  currency: string;
  serviceBaseMinor: number;
  totalProfessionalMinor: number;
  /** Set when API returns service breakdown (fixed + UR + VAT flags). */
  serviceFixedMinor?: number;
  serviceUrVariableMinor?: number;
  ivaOnFixedMinor?: number;
  ivaOnUrMinor?: number;
  urUnits?: number | null;
  urIndexMinorPerUnit?: number | null;
  fixedAmountIncludesIva?: boolean;
  urVariableIncludesIva?: boolean;
  /** Delivery netted off the base because the job is digital-only. */
  digitalDeliveryDeductionMinor?: number;
  professionalStampMinor?: number;
};

import type { UruguayBankAccountType } from "@gestionalo/shared-types";

export type ProfessionalBankAccountDto = {
  holderName: string;
  holderDocumentId: string;
  institutionCode: string;
  accountNumber: string;
  accountType: UruguayBankAccountType;
};

export type ProfessionalPayoutSummary = {
  currency: string;
  totalEarnedMinor: number;
  pendingPaymentMinor: number;
  paidOutMinor: number;
};

export type ProfessionalPayoutLineRow = {
  id: string;
  notarialRequestId: string | null;
  accountantRequestId: string | null;
  amountMinor: number;
  currency: string;
  payoutRequestId: string | null;
  payoutStatus: "pending" | "paid" | null;
  createdAt: string;
  requestLabel: string | null;
  requesterName: string | null;
};

export type ProfessionalPayoutRequestRow = {
  id: string;
  status: ProfessionalPayoutStatus;
  totalMinor: number;
  currency: string;
  createdAt: string;
  processingAt: string | null;
  paidAt: string | null;
  cancelledAt: string | null;
};

export async function getProfessionalBankAccount(): Promise<
  (ProfessionalBankAccountDto & { updatedAt: string }) | null
> {
  const res = await apiClient.get<
    (ProfessionalBankAccountDto & { updatedAt: string }) | null
  >("/me/professional/bank-account");
  return res.data;
}

export async function deleteProfessionalBankAccount(): Promise<void> {
  await apiClient.delete("/me/professional/bank-account");
}

export async function putProfessionalBankAccount(
  body: ProfessionalBankAccountDto
): Promise<ProfessionalBankAccountDto & { updatedAt: string }> {
  const res = await apiClient.put<ProfessionalBankAccountDto & { updatedAt: string }>(
    "/me/professional/bank-account",
    body
  );
  return res.data;
}

export async function getProfessionalPayoutSummary(): Promise<ProfessionalPayoutSummary> {
  const res = await apiClient.get<ProfessionalPayoutSummary>("/me/professional/payouts/summary");
  return res.data;
}

export async function listProfessionalPayoutLines(params: {
  limit?: number;
  offset?: number;
}): Promise<{ items: ProfessionalPayoutLineRow[]; total: number }> {
  const searchParams = new URLSearchParams();
  if (params.limit != null) searchParams.set("limit", String(params.limit));
  if (params.offset != null) searchParams.set("offset", String(params.offset));
  const q = searchParams.toString();
  const res = await apiClient.get<{ items: ProfessionalPayoutLineRow[]; total: number }>(
    `/me/professional/payouts/lines${q ? `?${q}` : ""}`
  );
  return res.data;
}

export async function listProfessionalPayoutRequests(params: {
  limit?: number;
  offset?: number;
}): Promise<{ items: ProfessionalPayoutRequestRow[]; total: number }> {
  const searchParams = new URLSearchParams();
  if (params.limit != null) searchParams.set("limit", String(params.limit));
  if (params.offset != null) searchParams.set("offset", String(params.offset));
  const q = searchParams.toString();
  const res = await apiClient.get<{ items: ProfessionalPayoutRequestRow[]; total: number }>(
    `/me/professional/payouts/requests${q ? `?${q}` : ""}`
  );
  return res.data;
}

export type ProfessionalPayoutEligibilityLine = {
  id: string;
  amountMinor: number;
  currency: string;
  createdAt: string;
  availableAt: string;
  notarialRequestId: string | null;
  accountantRequestId: string | null;
  requestLabel: string | null;
  requesterName: string | null;
};

export type ProfessionalPayoutEligibilityPendingRequest = {
  id: string;
  status: string;
  totalMinor: number;
  currency: string;
  createdAt: string;
};

export type ProfessionalPayoutEligibility = {
  currency: string | null;
  holdDays: number;
  availableMinor: number;
  lockedMinor: number;
  availableLines: ProfessionalPayoutEligibilityLine[];
  lockedLines: ProfessionalPayoutEligibilityLine[];
  pendingRequest: ProfessionalPayoutEligibilityPendingRequest | null;
};

export async function getProfessionalPayoutEligibility(): Promise<ProfessionalPayoutEligibility> {
  const res = await apiClient.get<ProfessionalPayoutEligibility>(
    "/me/professional/payouts/eligibility",
  );
  return res.data;
}

export async function requestProfessionalPayout(body?: {
  lineIds?: string[];
}): Promise<{
  id: string;
  status: string;
  totalMinor: number;
  currency: string;
  createdAt: string;
}> {
  const res = await apiClient.post<{
    id: string;
    status: string;
    totalMinor: number;
    currency: string;
    createdAt: string;
  }>("/me/professional/payouts/request", body ?? {});
  return res.data;
}

export type ProfessionalPayoutStatus = "pending" | "processing" | "paid" | "cancelled";

export type PlatformProfessionalPayoutListItem = {
  id: string;
  professionalUserId: string;
  professionalEmail: string | null;
  professionalName: string | null;
  status: ProfessionalPayoutStatus;
  totalMinor: number;
  currency: string;
  createdAt: string;
  processingAt: string | null;
  paidAt: string | null;
  cancelledAt: string | null;
};

export type PlatformProfessionalPayoutDetailProfessional = {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  fullName: string | null;
  phone: string | null;
  documentType: "national_id" | "passport" | null;
  documentNumber: string | null;
  cedula: string | null;
  notaryMatricula: string | null;
  role: "notary" | "accountant" | null;
};

export type PlatformProfessionalPayoutDetail = {
  id: string;
  status: ProfessionalPayoutStatus;
  totalMinor: number;
  currency: string;
  bankSnapshot: Record<string, string>;
  notes: string | null;
  createdAt: string;
  processingAt: string | null;
  processingByUserId: string | null;
  paidAt: string | null;
  paidByUserId: string | null;
  cancelledAt: string | null;
  cancelledByUserId: string | null;
  receiptFilename: string | null;
  receiptUploadedAt: string | null;
  hasReceipt: boolean;
  professional: PlatformProfessionalPayoutDetailProfessional;
  lines: Array<{
    id: string;
    notarialRequestId: string | null;
    accountantRequestId: string | null;
    requestLabel: string | null;
    requesterName: string | null;
    amountMinor: number;
    currency: string;
  }>;
};

export async function listPlatformProfessionalPayoutRequests(params: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: PlatformProfessionalPayoutListItem[]; total: number }> {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set("status", params.status);
  if (params.limit != null) searchParams.set("limit", String(params.limit));
  if (params.offset != null) searchParams.set("offset", String(params.offset));
  const q = searchParams.toString();
  const res = await apiClient.get<{ items: PlatformProfessionalPayoutListItem[]; total: number }>(
    `/platform/professional-payout-requests${q ? `?${q}` : ""}`
  );
  return res.data;
}

export async function getPlatformProfessionalPayoutRequest(
  id: string
): Promise<PlatformProfessionalPayoutDetail> {
  const res = await apiClient.get<PlatformProfessionalPayoutDetail>(
    `/platform/professional-payout-requests/${encodeURIComponent(id)}`
  );
  return res.data;
}

export type PlatformProfessionalPayoutTransitionResponse = {
  id: string;
  status: ProfessionalPayoutStatus;
  processingAt: string | null;
  paidAt: string | null;
  cancelledAt: string | null;
  receiptUploadedAt: string | null;
  receiptFilename: string | null;
  hasReceipt: boolean;
};

export async function transitionPlatformProfessionalPayout(
  id: string,
  body: { status: "processing" | "paid" | "cancelled"; reason?: string | null },
  receipt?: File,
): Promise<PlatformProfessionalPayoutTransitionResponse> {
  const url = `/platform/professional-payout-requests/${encodeURIComponent(id)}/transition`;
  if (body.status === "paid") {
    if (!receipt) throw new Error("A payment receipt file is required to mark a payout as paid");
    const form = new FormData();
    form.append("status", body.status);
    if (body.reason) form.append("reason", body.reason);
    form.append("receipt", receipt);
    const res = await apiClient.post<PlatformProfessionalPayoutTransitionResponse>(url, form);
    return res.data;
  }
  const res = await apiClient.post<PlatformProfessionalPayoutTransitionResponse>(url, body);
  return res.data;
}

export type PlatformProfessionalPayoutAttachReceiptResponse = {
  id: string;
  receiptFilename: string | null;
  receiptUploadedAt: string | null;
  hasReceipt: boolean;
};

export async function attachPlatformProfessionalPayoutReceipt(
  id: string,
  receipt: File,
): Promise<PlatformProfessionalPayoutAttachReceiptResponse> {
  const form = new FormData();
  form.append("receipt", receipt);
  const res = await apiClient.post<PlatformProfessionalPayoutAttachReceiptResponse>(
    `/platform/professional-payout-requests/${encodeURIComponent(id)}/receipt`,
    form,
  );
  return res.data;
}

export async function fetchPlatformProfessionalPayoutReceiptBlob(id: string): Promise<Blob> {
  const res = await apiClient.get<Blob>(
    `/platform/professional-payout-requests/${encodeURIComponent(id)}/receipt`,
    { responseType: "blob" },
  );
  return res.data;
}

/**
 * The professional's own fiscal declaration — RUT, razón social, and whether
 * they issue CFEs.
 *
 * SESSION-addressed, unlike the invitation endpoints. Those are reachable only
 * during onboarding, which left every already-active professional with no way
 * to declare — notaries most sharply, since theirs happens at account creation
 * and that moment has passed for all of them.
 */
export type ProfessionalFiscalDeclarationState = {
  complete: boolean;
  rut: string | null;
  razonSocial: string | null;
  issuesCfe: boolean | null;
  cfeProvider: string | null;
};

/**
 * The three states every fiscal surface on the platform reports.
 *
 * ONE COPY, DELIBERATELY. The completeness rule lives in
 * `isFiscalDeclarationComplete` on the backend and has already been re-encoded
 * four times across this codebase, each copy eventually disagreeing with the
 * gate that actually refuses bids. So this reads the SERVER's `complete` and
 * only classifies the remainder: an explicit `false` on issuesCfe is an
 * ANSWER, and only `null` means never asked.
 *
 * The distinction is the whole point of the tri-state. `not_declared` is
 * someone who owes us a form; `cannot_issue_cfe` is someone who filled it in
 * and told us no. Warning the second one is nagging them for work they have
 * already done.
 */
export type ProfessionalFiscalStatus = "complete" | "cannot_issue_cfe" | "not_declared";

export function professionalFiscalStatus(
  data: ProfessionalFiscalDeclarationState | undefined | null,
): ProfessionalFiscalStatus {
  if (data?.complete === true) return "complete";
  if (data?.issuesCfe === false) return "cannot_issue_cfe";
  return "not_declared";
}

export async function getMyFiscalDeclaration(): Promise<ProfessionalFiscalDeclarationState> {
  const res = await apiClient.get<ProfessionalFiscalDeclarationState>(
    "/me/professional/fiscal-declaration",
  );
  return res.data;
}

export async function saveMyFiscalDeclaration(declaration: {
  rut: string;
  razonSocial: string;
  issuesCfe: boolean;
  cfeProvider?: string;
}): Promise<void> {
  await apiClient.put("/me/professional/fiscal-declaration", declaration);
}

/** Why a professional cannot act on an RFQ job yet (spec 300908fc slice 3b, D3). */
export type RfqGateBlocker = "fiscal_declaration" | "consumer_cfe";

export type RfqConsumerCfeGateStatus =
  | { blocked: false }
  | { blocked: true; legId: string; blockers: RfqGateBlocker[] };

/**
 * Is this professional blocked on this job, and by what?
 *
 * ASKED OF THE SERVER rather than derived on the page. Every write behind the
 * request detail page is gated server-side; a screen that decided this for
 * itself would eventually disagree with the gate that enforces it, and put a
 * modal over an action that would have worked — or none over one that will not.
 */
export async function getRfqGate(
  requestId: string,
): Promise<RfqConsumerCfeGateStatus> {
  const res = await apiClient.get<RfqConsumerCfeGateStatus>(
    `/me/professional/requests/${requestId}/rfq-gate`,
  );
  return res.data;
}

export type ConsumerCfeObligation = {
  legId: string;
  discipline: "notary" | "accountant";
  /** What the CFE must total, to the centésimo. */
  expectedTotalMinor: number;
  currency: "UYU";
  recorded: {
    documentUrl: string;
    declaredTotalMinor: number;
    recordedAt: string;
  } | null;
};

/** What this leg's CFE must total, and whether one is already on file. */
export async function getConsumerCfeObligation(
  legId: string,
): Promise<ConsumerCfeObligation> {
  const res = await apiClient.get<ConsumerCfeObligation>(
    `/me/professional/quote-legs/${legId}/consumer-cfe`,
  );
  return res.data;
}

/**
 * Record the CFE issued to the consumer.
 *
 * multipart, because the platform STORES the document rather than linking to
 * the professional's CFE provider. The declared total rides along as a form
 * field and the server compares it EXACTLY against what the consumer paid —
 * a mismatch comes back as a 400 naming the figure to use.
 */
export async function recordConsumerCfe(
  legId: string,
  params: { declaredTotalMinor: number; file: File },
): Promise<ConsumerCfeObligation> {
  const form = new FormData();
  form.append("file", params.file);
  form.append("declaredTotalMinor", String(params.declaredTotalMinor));
  const res = await apiClient.put<ConsumerCfeObligation>(
    `/me/professional/quote-legs/${legId}/consumer-cfe`,
    form,
  );
  return res.data;
}
